import {Component, OnInit, ViewChild} from '@angular/core';
import {NgSelectizeComponent} from 'ng-selectize';
import {forkJoin} from 'rxjs';
import {ApiService, HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {NavigationService} from '../../shared/navigation.service';

@Component({
  selector: 'invoice-settings-form',
  templateUrl: './invoice-settings-form.component.html',
  styleUrls: ['../styles/account-form.scss', './invoice-settings-form.component.scss']
})
export class InvoiceSettingsFormComponent implements OnInit {

  @ViewChild('selectizeRecipients') selectizeRecipients: NgSelectizeComponent;

  isLoading = false;
  isAddRecipientsShowing = false;
  isUsingElectronicInvoicing = false;
  isAttachTimecards = false;
  recipients: string[] = [];
  recipientOptions: Option[] = [];
  masquerading$ = this.Auth.masquerading$;

  selectizeConfig = {
    create: true,
    maxItems: null,
    highlight: false,
    persist: false,
    createFilter: new RegExp('(.+)@(.+)'),
    plugins: ['remove_button'],

    onDropdownOpen(dropdown) {
      dropdown.remove();
    }
  };

  constructor(private Api: ApiService,
              private Navigation: NavigationService,
              private Auth: AuthService) {
  }

  get hasRecipients() {
    return this.recipients.length > 0;
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isLoading = true;

    forkJoin(
      this.Api.getInvoiceSettings(),
      this.Api.getInvoiceRecipients()
    ).subscribe(([settingsResponse, recipientsResponse]) => {
      this.initializeForm(settingsResponse, recipientsResponse);
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  initializeForm(settingsResponse: InvoiceSettings, recipientsResponse: InvoiceRecipients) {
    this.isAttachTimecards = settingsResponse.isAttachTimecards;
    this.isUsingElectronicInvoicing = settingsResponse.isUsingElectronicInvoicing;
    this.recipients = recipientsResponse.recipients;
    this.populateRecipientOptions();
  }

  updateAttachTimecards() {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.isLoading = true;

    this.Api.updateInvoiceSettings({isAttachTimecards: this.isAttachTimecards}).subscribe(() => {
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  cancel() {
    this.restoreRecipients();
    this.isAddRecipientsShowing = false;
  }

  save() {
    this.saveRecipients();
    this.isAddRecipientsShowing = false;
  }

  restoreRecipients() {
    this.recipients = [];

    for (const option of this.recipientOptions) {
      this.recipients.push(option.text);
    }
  }

  saveRecipients() {
    this.populateRecipientOptions();
    this.updateRecipients();
  }

  populateRecipientOptions() {
    this.recipientOptions = [];

    for (const recipient of this.recipients) {
      this.recipientOptions.push(new Option(recipient));
    }
  }

  removeRecipient(recipient: string) {
    if (this.masquerading$.getValue()) {
      return;
    }
    const index = this.recipients.indexOf(recipient);

    this.recipients.splice(index, 1);
    this.recipientOptions.splice(index, 1);

    this.updateRecipients();
  }

  updateRecipients() {
    this.isLoading = true;

    this.Api.updateInvoiceRecipients({recipients: this.recipients}).subscribe(() => {
      this.isLoading = false;
    }, (err: HttpError) => {
      this.handleError(err);
    });
  }

  openInvoiceRecipients() {
    this.isAddRecipientsShowing = true;
  }

  isSaveButtonDisabled() {
    if (this.recipients.length !== this.recipientOptions.length) {
      return false;
    }

    for (let i = 0; i < this.recipients.length; i++) {
      if (this.recipients[i] !== this.recipientOptions[i].text) {
        return false;
      }
    }

    return true;
  }

  handleError(err: HttpError) {
    this.isLoading = false;
    this.Navigation.handleError(err);
  }
}

class Option {
  text: string;
  value: string;

  constructor(name) {
    this.text = name;
    this.value = name;
  }
}
